import { ProductCardProduct } from 'shared/types/product/Product';
import { Variant } from 'shared/types/product/Variant';

/**
 * Check if a variant should be displayed based on discontinued/stock status
 *
 * Business Rules:
 * - Show if not discontinued
 * - Show if discontinued AND in stock with available quantity
 * - Hide if discontinued AND (not in stock OR no available quantity)
 *
 * @param variant - The variant to check
 * @returns true if the variant should be displayed
 */
export function isVariantDisplayable(variant: Variant | undefined): boolean {
  if (!variant) return false;

  const isDiscontinued = variant.attributes?.isDiscontinued ?? false;
  const isOnStock = variant.isOnStock ?? false;
  const availableQuantity = variant.availableQuantity ?? 0;

  return !isDiscontinued || (isDiscontinued && isOnStock && availableQuantity > 0);
}

/**
 * Filter products to only show those with displayable variants
 *
 * @param products - Array of products to filter
 * @returns Filtered array of products that have at least one displayable variant
 */
export function filterDisplayableProducts(products: ProductCardProduct[]): ProductCardProduct[] {
  return products.filter((product) => {
    if (isVariantDisplayable(product.masterVariant)) {
      return true;
    }

    return product.variants?.some(isVariantDisplayable) ?? false;
  });
}

/**
 * Get the displayable variant for a product (used in collections)
 *
 * Priority:
 * 1. Preferred color variant (if displayable)
 * 2. Master variant (if displayable)
 * 3. First displayable variant
 * 4. null if no displayable variants
 *
 * @param product - The product
 * @param preferredColor - Optional color to match
 * @returns The variant to display, or null if none are displayable
 */
export function getDisplayableVariant(product: ProductCardProduct, preferredColor?: string): Variant | null {
  if (preferredColor) {
    const colorMatch = product.variants?.find((v) => v.attributes?.actualColor === preferredColor);
    if (colorMatch && isVariantDisplayable(colorMatch)) {
      return colorMatch;
    }
  }

  // Fall back to master variant
  if (isVariantDisplayable(product.masterVariant)) {
    return product.masterVariant ?? null;
  }

  const firstDisplayable = product.variants?.find(isVariantDisplayable);
  return firstDisplayable || null;
}

/**
 * Map products to their displayable variants with optional color matching
 * Returns an array of objects containing the product, displayable variant, and matched color
 * Filters out products with no displayable variants
 *
 * @param products - Array of products
 * @param colorMatcher - Optional. Determines preferred color for each product
 * @returns Array of objects with product, displayVariant, and defaultColor
 */
export function mapProductsToDisplayableVariants(
  products: ProductCardProduct[],
  colorMatcher?: (product: ProductCardProduct) => string | undefined,
): Array<{ product: ProductCardProduct; displayVariant: Variant; defaultColor?: string }> {
  return products.flatMap((product) => {
    const defaultColor = colorMatcher?.(product);
    const displayVariant = getDisplayableVariant(product, defaultColor);

    if (!displayVariant) return [];

    return [
      {
        product,
        displayVariant,
        defaultColor,
      },
    ];
  });
}
